import { useClickOutside } from "@/hooks/useClickOutside";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Chip } from "./ui/chip";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  id: string;
  placeholder: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  disabled?: boolean;
  disabledHint?: string;
  emptyText?: string;
  /** Rótulo acessível do controle (usado no `aria-label` do listbox). */
  ariaLabel?: string;
}

/**
 * Multi-seleção com lista local de opções (checkbox), busca client-side
 * opcional e chips removíveis. Usada para situação, UF, município e porte.
 */
export function MultiSelect({
  id,
  placeholder,
  options,
  selected,
  onChange,
  searchable = false,
  searchPlaceholder,
  disabled = false,
  disabledHint,
  emptyText = "Nenhuma opção encontrada",
  ariaLabel,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useClickOutside(containerRef, () => setOpen(false), open);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    searchRef.current?.focus();
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const optionByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );

  const visibleOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!searchable || trimmed === "") {
      return options;
    }
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(trimmed) ||
        option.value.toLowerCase().includes(trimmed),
    );
  }, [options, searchable, query]);

  const toggleValue = (value: string) => {
    if (selectedSet.has(value)) {
      onChange(selected.filter((current) => current !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const triggerText =
    disabled && disabledHint
      ? disabledHint
      : selected.length === 0
        ? placeholder
        : `${selected.length} ${selected.length === 1 ? "selecionado" : "selecionados"}`;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          disabled && "cursor-not-allowed opacity-60",
          selected.length === 0 && "text-muted-foreground",
        )}
      >
        <span className="truncate">{triggerText}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>

      {!disabled && selected.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selected.map((value) => (
            <Chip
              key={value}
              label={optionByValue.get(value)?.label ?? value}
              onRemove={() => toggleValue(value)}
            />
          ))}
        </div>
      ) : null}

      {open && !disabled ? (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
          {searchable ? (
            <div className="border-b border-border p-2">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={searchPlaceholder ?? "Filtrar…"}
                  className="h-9 w-full rounded-md border border-input bg-card pl-8 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </div>
          ) : null}
          <div
            role="listbox"
            aria-multiselectable="true"
            aria-label={ariaLabel ?? placeholder}
            className="max-h-64 overflow-y-auto p-1"
          >
            {visibleOptions.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyText}</p>
            ) : (
              visibleOptions.map((option) => {
                const isSelected = selectedSet.has(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => toggleValue(option.value)}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-muted"
                  >
                    <span
                      className={cn(
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                        isSelected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-slate-300",
                      )}
                    >
                      {isSelected ? <Check className="h-3 w-3" aria-hidden="true" /> : null}
                    </span>
                    <span className="truncate">{option.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
